// Libraries
import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { Radio as BaseRadio } from 'react-radio-group';
import cn from 'classnames';
// Data && utils
import useOutsideClick from 'src/hooks/useOutsideClick';
import useToggle from 'src/hooks/useToggle';
import { stopImmediatePropagation } from 'src/utils/event';
import { RADIO_BUTTON_SIZE, HINT_ICON_ID } from './constants';
// Components
import { FreeFormTextLabel, StaticTextLabel } from './Parts';
// Styles
import styles from './styles.scss';

const Hint = ({ hint }) => {
  const appliedStyles = cn(styles.additionalInfo, 'caption');
  return <div className={appliedStyles}>{hint}</div>;
};

const Content = ({
  containerStyles,
  hint,
  label = '',
  setSelectedAnswer,
  adjValue,
  showHint = false,
  showInput = false,
  size = RADIO_BUTTON_SIZE.LARGE,
  value,
  ...rest
}) => {
  /* get additionalInfo, if any */
  const { additionalAnswerInfo = {}, launchAdditionalInfoDisplay = () => {} } = rest;
  const showAdditionalInfo = Boolean(Object.keys(additionalAnswerInfo).length);
  const acceptCustomerInput = showInput;
  const hasHint = Boolean(hint);
  const showMoreInfo = additionalAnswerInfo && Boolean(additionalAnswerInfo.showModal);
  const contentRef = useRef(null);
  const appliedStylesV1 = cn({
    [styles.medium]: size === RADIO_BUTTON_SIZE.MEDIUM,
    [styles.large]: size === RADIO_BUTTON_SIZE.LARGE,
  });

  const appliedStylesV2 = cn({
    [styles.mediumV2]: size === RADIO_BUTTON_SIZE.MEDIUM,
    [styles.largeV2]: size === RADIO_BUTTON_SIZE.LARGE,
  });

  const appliedStyles = showAdditionalInfo ? appliedStylesV2 : appliedStylesV1;

  const handleContentClick = () => {
    setSelectedAnswer({
      value,
      stop: hasHint || acceptCustomerInput,
      static: !acceptCustomerInput,
    });
    if (contentRef.current) {
      contentRef.current.focus();
    }
  };

  // Older QA flow for ids [13, 38, 1034]
  const radioPanelBasic = acceptCustomerInput ? (
    <FreeFormTextLabel
      value={value}
      showInput={showInput}
      setSelectedAnswer={setSelectedAnswer}
      {...rest}
    />
  ) : (
    <StaticTextLabel
      label={label}
      adjValue={adjValue}
      hasHint={hasHint}
      showMoreInfo={showMoreInfo}
      onClick={stopImmediatePropagation(() =>
        launchAdditionalInfoDisplay(additionalAnswerInfo.additionalInfoRef, value),
      )}
      {...rest}
    />
  );

  // QA flow for id [879]
  const radioPanelWithImage = (
    <>
      {additionalAnswerInfo.imageRef && (
        <div className={styles.additionalAnswerInfo}>
          <img src={additionalAnswerInfo.imageRef} />
        </div>
      )}
    </>
  );
  const contentStyles = cn( 'p2 n700', {
    'paddingTop-small': !additionalAnswerInfo.imageRef,
  });

  return (
    <div className={cn('h6', containerStyles, styles.label, appliedStyles)}>
      <div onClick={handleContentClick} ref={contentRef}>
        <div className={styles.innerLabel}>{radioPanelBasic}</div>
        {showAdditionalInfo && radioPanelWithImage}
        {additionalAnswerInfo.content && (
          <p className={contentStyles}>{additionalAnswerInfo.content}</p>
        )}
      </div>
      {showHint && <Hint hint={hint} />}
    </div>
  );
};

const RadioV2QA = ({ label, hint, setElementClicked, value, ...rest }) => {
  const containerRef = useRef(null);
  const labelRef = useRef(null);
  const { toggleState: isDisabled, setBooleanToggle: setIsDisabled } = useToggle(true);
  const enableEditing = () => setIsDisabled(false);
  const disableEditing = () => setIsDisabled(true);

  const { toggleState: showHint, setBooleanToggle: setShowHint, toggle: toggleHint } = useToggle(
    false,
  );
  const openHint = () => {
    if (hint) setShowHint(true);
  };
  const closeHint = () => setShowHint(false);
  const handleClick = (e) => {
    setElementClicked(e.target);
    const isHintIcon = e.target.getAttribute(HINT_ICON_ID);
    if (isHintIcon) toggleHint();
    if (!isHintIcon) {
      labelRef.current.click();
      openHint();
    }
    enableEditing();
  };

  useOutsideClick(containerRef, closeHint);
  useOutsideClick(containerRef, disableEditing);
  const targetId = `answer${value}`;

  return (
    <div className={styles.radioContainer} onClick={handleClick} ref={containerRef}>
      <BaseRadio value={value} id={targetId} />
      <label htmlFor={targetId} ref={labelRef} />
      <Content
        label={label}
        hint={hint}
        showHint={showHint}
        isDisabled={isDisabled}
        disable={disableEditing}
        value={value}
        {...rest}
      />
    </div>
  );
};

Hint.propTypes = { hint: PropTypes.string.isRequired };

Content.propTypes = {
  containerStyles: PropTypes.string,
  hint: PropTypes.string,
  label: PropTypes.string.isRequired,
  setSelectedAnswer: PropTypes.func.isRequired,
  showHint: PropTypes.func.isRequired,
  showInput: PropTypes.bool,
  size: PropTypes.string.isRequired,
  value: PropTypes.any,
  adjValue: PropTypes.any,
};

RadioV2QA.propTypes = {
  label: PropTypes.string.isRequired,
  hint: PropTypes.string,
  setElementClicked: PropTypes.func.isRequired,
  value: PropTypes.any,
  additionalAnswerInfo: PropTypes.shape({
    imageRef: PropTypes.string,
    content: PropTypes.string,
    additionalInfoRef: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
};

export default RadioV2QA;

/*
  RadioV2QA: A styled radio element meant to work wigh RadioGroupV2QA;
    - The function setSelectedAnswer communicates the client's selection up to RadioGroupV2QA

  Props:
    - containerStyles: Style the element directly
    - size: 'medium' or 'large'
    - label: The text rendered
    - value: Value of the radio
*/
